import { DOCUMENT } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRoute, ParamMap } from '@angular/router';
import {
  catchError, combineLatest, from,
  map,
  Observable,
  of, shareReplay,
  Subject,
  take,
  takeUntil,
} from 'rxjs';

import { ConfigurationService, IProviderShareableLinkAppConfig } from '@sondermind/configuration';
import { GtmCustomEvents, GtmDataLayerService } from '@sondermind/google-tag-manager';
import {
  IRequestSessionParams, RequestASessionService
} from '@sondermind/features/public-facing/session-request';
import {
  IPslRedesignFeatureFlagData, LAUNCH_DARKLY_SERVICE, LaunchDarklyService, LDBooleanFeatureFlags, LDJsonFeatureFlags
} from '@sondermind/launch-darkly';
import { SegmentService } from '@sondermind/segment';
import { IAPIFetchResponse } from '@sondermind/utilities/models-http';
import { ISessionRequestPreferences } from '@sondermind/utilities/models-scheduling';
import { WINDOW } from '@sondermind/utilities/tools';
import { ILocation, IProviderShareableProfile } from '@sondermind/utilities/models-public-facing';
import {
  IAppointmentSlot,
  ProviderCalendarHttpService
} from '@sondermind/data-access/public-facing/provider-calendar';
import { SessionPreference } from '@sondermind/utilities/models-session';

import { IPslPageLoadEventData } from '../../models/segment-event-data.interface';
import {
  IPslSegmentSchema,
  PslSegmentEvents,
} from '../../models/segment/psl-segment-schema.interface';
import { ShareableLinksHttpService } from '../../shareable-links.httpservice';
import { LandingPageContact } from '../../models/enums/landing-page-contact.enum';
import { ITopSpecialty } from '../../models/top-specialty.interface';

@Component({
  selector: 'landing-page',
  templateUrl: './landing-page.component.html',
  styleUrls: ['./landing-page.component.scss'],
})

export class LandingPageComponent {
  providerShareableProfile: IProviderShareableProfile = {} as IProviderShareableProfile;
  isValid: boolean = false;
  isLoading: boolean = true;

  therapyMatchFlowUrl: string = 'https://start.sondermind.com';

  soonestAvailableDate$: Observable<Date | null>;
  paramsShareCode: string = '';
  paramsName: string = '';
  requestSessionQueryParams: IRequestSessionParams = null; // For tracking the user coming from .com site
  topSpecialties: ITopSpecialty[] = [];
  providerPrimaryAddress: ILocation = null;

  queryParamMap!: ParamMap;

  useCheckoutFormFf$: Observable<boolean>;
  pslRedesignFf$: Observable<IPslRedesignFeatureFlagData>;
  alwaysShowAcceptingFf$: Observable<boolean>;

  appointmentSlots$!: Observable<IAppointmentSlot[]>;
  isAppointmentCarouselLoading: boolean = true;
  showProviderCalendar: boolean = true;

  destroyed$ = new Subject<void>();

  supportPhoneNumberLink: string = LandingPageContact.MATCH_BY_PHONE_LINK;
  supportPhoneNumberDisplay: string = LandingPageContact.MATCH_BY_PHONE_DISPLAY;

  constructor(
    private route: ActivatedRoute,
    private shareableLinksHttpService: ShareableLinksHttpService,
    private configurationService: ConfigurationService<IProviderShareableLinkAppConfig>,
    private titleService: Title,
    private metaService: Meta,
    private gtmDataLayerService: GtmDataLayerService,
    private providerCalendarHttpService: ProviderCalendarHttpService,
    private segmentService: SegmentService<IPslSegmentSchema>,
    private requestASessionService: RequestASessionService,
    @Inject(DOCUMENT) private document: Document,
    @Inject(LAUNCH_DARKLY_SERVICE) private launchDarklyService: LaunchDarklyService,
    @Inject(WINDOW) private window: Window,
    private providerCalendarService: ProviderCalendarHttpService
  ) {}

  ngOnInit(): void {
    combineLatest([this.route.paramMap, this.route.queryParamMap]).pipe(
      take(1),
    ).subscribe(([routeParams, queryParams]) => {
      this.queryParamMap = queryParams;
      this.therapyMatchFlowUrl = this.configurationService.env.app.flowHost;

      this.paramsShareCode = this.requestASessionService.getShareCodeFromParams(routeParams);
      this.paramsName = this.requestASessionService.getNameFromParams(routeParams);
      this.requestSessionQueryParams = this.requestASessionService.parseQueryParams(queryParams);

      this.shareableLinksHttpService.fetch$(this.paramsShareCode).pipe(
        takeUntil(this.destroyed$),
        catchError((error) => of(error))
      ).subscribe((response: IAPIFetchResponse<IProviderShareableProfile>) => {
        if (Object.prototype.hasOwnProperty.call(response, 'error')) {
          this.isLoading = false;
          return;
        }

        this.providerShareableProfile = response.data;
        this.gtmDataLayerService.dataLayer = {
          event: GtmCustomEvents.PROVIDER_PAGE_VIEW,
          id: this.providerShareableProfile.id,
          // eslint-disable-next-line @typescript-eslint/naming-convention
          full_name: this.providerShareableProfile.full_name
        };
        this.sendPageViewSegmentEvent();

        this.updateMetaInfo(
          this.providerShareableProfile.display_name,
          this.providerShareableProfile.certifications,
          this.providerShareableProfile.bio
        );

        this.updateCanonicalUrl(window.location.href);

        this.isValid = true;
        this.isLoading = false;

        this.topSpecialties = this.sortTopSpecialties(this.providerShareableProfile);
        this.providerPrimaryAddress = this.findPrimaryInPersonAddress(this.providerShareableProfile);
        this.soonestAvailableDate$ = this.providerCalendarHttpService
          .getSoonestAvailableDates$([this.providerShareableProfile.id])
          .pipe(
            map((data) => data[0].soonest_availability),
            shareReplay(1)
          );

        /** TODO: Uncomment before release
        this.appointmentSlots$ = this.providerCalendarService.getAppointmentSlots$(this.providerShareableProfile.id)
          .pipe(
            map((apptSlots: IAppointmentSlot[]) => {
              // toggle loading
              // wrapped in setTimeout to avoid ExpressionChangedAfterItHasBeenCheckedError
              // TODO: clean this up in ORCA-1270
              setTimeout(() => { this.isAppointmentCarouselLoading = false }, 0);

              if (apptSlots && apptSlots.length > 0) {
                // return the appointment slots
                return apptSlots;
              }

              // otherwise, hide the calendar (and show the connect card instead)
              // wrapped in setTimeout to avoid ExpressionChangedAfterItHasBeenCheckedError
              // TODO: clean this up in ORCA-1270
              setTimeout(() => { this.showProviderCalendar = false }, 0);

              return [];
            })
          );
        */
      });
    });

    this.checkFeatureFlags();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private findPrimaryInPersonAddress(profile: IProviderShareableProfile): ILocation {
    return profile.locations.find((location) => location.office_session_default === true);
  }

  private sortTopSpecialties(profile: IProviderShareableProfile): ITopSpecialty[] {
    const queryParamSpecialties = this.queryParamMap.get('specialties')?.split(',') ?? [];

    return profile.top_specialties
      .map((topSpecialty) => {
        const specialty = this.providerShareableProfile.specialties.find((s) => topSpecialty.id === s.id);
        return {
          ...specialty,
          isQueryParam: queryParamSpecialties.includes(specialty.id.toString()),
          priorityOrder: topSpecialty.priority_order
        };
      })
      .sort(this.topSpecialtyCompareFn);
  }

  // sort by priorityOrder if available, otherwise sort alphabetically
  private topSpecialtyCompareFn(a: ITopSpecialty, b: ITopSpecialty) {
    if (a.priorityOrder !== null && b.priorityOrder !== null) {
      return a.priorityOrder - b.priorityOrder;
    }
    if (a.priorityOrder !== null && b.priorityOrder === null) {
      return -1;
    }
    if (a.priorityOrder === null && b.priorityOrder !== null) {
      return 1;
    }
    return a.name.localeCompare(b.name);
  }

  private checkFeatureFlags(): void {
    this.useCheckoutFormFf$ = this.launchDarklyService
      .checkFlagEnabled$(LDBooleanFeatureFlags.PSL_USE_CHECKOUT_FORM)
      .pipe(shareReplay(1));

    this.pslRedesignFf$ = this.launchDarklyService
      .checkFlagJsonValue$<IPslRedesignFeatureFlagData>(LDJsonFeatureFlags.PSL_PAGE_REDESIGN)
      .pipe(shareReplay(1));

    this.alwaysShowAcceptingFf$ = this.launchDarklyService
      .checkFlagEnabled$(LDBooleanFeatureFlags.PSL_ALWAYS_SHOW_ACCEPTING)
      .pipe(shareReplay(1));
  }

  onRequestSessionButtonClicked(
    requestedSessionPreferences?: ISessionRequestPreferences
  ): void {
    this.requestASessionService.onRequestSessionButtonClicked({
      profile: this.providerShareableProfile,
      queryParams: this.requestSessionQueryParams,
      requestedSessionPreferences: {
        startTime: requestedSessionPreferences?.startTime,
        endTime: requestedSessionPreferences?.endTime,
        sessionType: requestedSessionPreferences?.sessionType ?? SessionPreference.NO_PREFERENCE
      }
    });
  }

  private sendPageViewSegmentEvent(): void {
    const activeLaunchDarklyFlags: { [key: string]: unknown; } = {};
    this.launchDarklyService.getAllFlags$().pipe(take(1)).subscribe((flags) => {
      // For every active FF we want to track on this page, add the key + FF value to the map
      const trackingFlags = [
        LDBooleanFeatureFlags.FIND_PROVIDER_PUBLIC_HEADER_REDIRECT,
        LDBooleanFeatureFlags.PSL_SEND_REQUEST_A_SESSION_EMAIL,
        LDJsonFeatureFlags.PSL_PAGE_REDESIGN,
        LDBooleanFeatureFlags.PSL_ALWAYS_SHOW_ACCEPTING,
        LDBooleanFeatureFlags.PSL_USE_CHECKOUT_FORM,
      ];
      trackingFlags.forEach((flagName) => {
        activeLaunchDarklyFlags[flagName] = flags[flagName] ?? false;
      });

      // Get UTM parameters
      const urlParams = this.route.snapshot.queryParams;
      const utmParams = Object.entries(urlParams).filter(([key, _value]) => key.startsWith('utm'));

      // Get params from directory, if they exist
      const directoryFilters = {
        state: this.queryParamMap.get('state'),
        insurance: this.queryParamMap.get('paymentTag'),
        modalities: this.queryParamMap.get('modalities'),
        specialties: this.queryParamMap.get('specialties'),
        genders: this.queryParamMap.get('genders'),
      };

      // Cut off any query params
      const referringDomain = this.document.referrer.slice(0, this.document.referrer.indexOf('?'));
      const referringUrl: string = this.queryParamMap.get('referringUrl') ?? '';

      const eventData = {
        accepting: this.providerShareableProfile.accepting_clients,
        activeLaunchDarklyFlags,
        directoryFilters,
        internalReferringPage: referringUrl,
        personaProviderId: this.providerShareableProfile.id,
        referringDomain,
        utm: utmParams,
      } as IPslPageLoadEventData;

      from(this.segmentService.track<PslSegmentEvents.PSL_PAGE_VIEW>(
        PslSegmentEvents.PSL_PAGE_VIEW,
        eventData
      )).subscribe();
    });
  }

  private updateMetaInfo(
    providerName: string,
    certifications: Array<{ name: string; abbrev: string; }>,
    bio: string
  ): void {
    const certs: string = certifications?.map((obj) => obj.name).join(', ');
    const title = `${providerName}, ${certs} | SonderMind`;
    // extract the first sentence of the bio;
    const sentence = /[^.!?]*[.!?]/.exec(bio);
    const firstSentenceofBio = sentence ? sentence[0] : bio;
    const first160CharsofBio = firstSentenceofBio ? firstSentenceofBio.substring(0, 159) : ''; // bio can be null
    const description = `${firstSentenceofBio ?? ''}`;

    this.titleService.setTitle(title);
    this.metaService.updateTag({ name: 'description', content: description });
    this.metaService.updateTag({ property: 'og:url', content: this.window.location.href });
    this.metaService.updateTag({ property: 'og:title', content: title });
    this.metaService.updateTag({ property: 'og:description', content: first160CharsofBio });
    this.metaService.updateTag({ property: 'twitter:url', content: this.window.location.href });
    this.metaService.updateTag({ name: 'twitter:title', content: title });
    this.metaService.updateTag({ name: 'twitter:description', content: first160CharsofBio });
  }

  private updateCanonicalUrl(url: string): void {
    let link: HTMLLinkElement = this.document.querySelector('link[rel="canonical"]');
    if (link) {
      link.href = url;
    } else {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      link.setAttribute('href', url);
      this.document.head.appendChild(link);
    }
  }
}
